// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Browser entry point for the songbook SPA.
 *
 * Bootstraps from server-provided InitialState (window.__SB_STATE__),
 * configures i18n, sets up view navigation, and wires the DOM shell and
 * PDF lifecycle management.
 */
import type { InitialState } from "../../shared/src/types";
import { Logger, LogLevel } from "../../shared/src/generic/logger";
import { AppShell } from "./app-shell";
import { initializeViewportHeightOnce } from "./device";
import { I18nManager } from "./i18n";
import { IndexManager } from "./index-manager";
import { PdfManager, pdfjsViewerLogger } from "./pdf-manager";
import { SearchManager } from "./search-manager";
import { SongIndexClient } from "./song-index-client";
import { SongManager } from "./song-manager";
import { SEARCH_PARAM_KEYS } from "../../shared/src/view-url";
import { ViewRouter } from "./router";
import { ThemeManager } from "./theme";
import { PdfjsViewerRuntime } from "@unilarva/pdfjs-viewer";
import * as legacyPdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { registerOfflineSupport } from "./service-worker-registration";

const logLevelStorageKey = "sb_loglevel";
const storedLogLevel = window.localStorage.getItem(logLevelStorageKey)?.toUpperCase();
const configuredLogLevel = storedLogLevel
  ? LogLevel[storedLogLevel as keyof typeof LogLevel]
  : undefined;

// Set `localStorage.sb_loglevel` to a named level (for example, `DEBUG`) before reloading.
Logger.setMinLevel(typeof configuredLogLevel === "number" ? configuredLogLevel : LogLevel.ERROR);

registerOfflineSupport();

declare global {
  interface Window {
    __SB_STATE__?: InitialState;
  }
}

const state: InitialState = (() => {
  const s = window.__SB_STATE__;
  if (!s) throw new Error("Missing window.__SB_STATE__ (SSR payload)");
  return s;
})();

// Capture initial viewport height once for shared mobile keyboard heuristics.
initializeViewportHeightOnce();

const i18nManager = new I18nManager(state);
const pdfRuntime = new PdfjsViewerRuntime({
  pdfjs: legacyPdfjs,
  workerSrc: "/assets/pdf.worker.min.mjs",
  logger: pdfjsViewerLogger,
});
const songIndexClient = new SongIndexClient(state);
const pdfManager = new PdfManager(state, i18nManager.translator, pdfRuntime, songIndexClient);
const searchManager = new SearchManager(
  state,
  i18nManager.translator,
  i18nManager.binding,
  songIndexClient,
);
const indexManager = new IndexManager(
  state,
  i18nManager.translator,
  i18nManager.binding,
  songIndexClient,
);
const songManager = new SongManager(
  state,
  i18nManager.translator,
  i18nManager.binding,
  songIndexClient,
);
const appShell = new AppShell(
  state,
  i18nManager.translator,
  i18nManager.binding,
  pdfManager,
  searchManager,
  indexManager,
  songManager,
);
const themeManager = new ThemeManager(state, i18nManager.translator);
// Language-bearing anchors support SSR/native navigation, not SPA history state.
const router = new ViewRouter({
  viewQueryParams: { song: ["id"], search: SEARCH_PARAM_KEYS },
});
(window as any).__sbViewRouter = router;

const appRoot = document.getElementById("app");
if (!appRoot) throw new Error("Missing #app");

const initialView = router.getCurrentViewFromUrl();

// Initialize DOM shell and static UI behavior.
appShell.init(appRoot, initialView);

// Wire i18n after the shell exists so the package can bind its native language select.
i18nManager.init(() => {
  appShell.refreshTitlesForCurrentView();
});

themeManager.init();

// Apply language precedence from URL or browser default once.
i18nManager.applyInitialLanguageFromUrlOrBrowser();

// Normalize the URL so only the recognized query parameters remain.
router.normalizeCurrentUrl(initialView);

// Subscribe to view changes from the router.
router.onViewChange(view => {
  appShell.showView(view);
});

// Start routing and activate the initial view.
router.start();
appShell.showView(initialView);

// On non-PDF views, warm up the PDF.js worker in the background after initial load.
if (
  initialView === "home" ||
  initialView === "search" ||
  initialView === "index" ||
  initialView === "song"
) {
  window.addEventListener("load", () => {
    window.setTimeout(() => {
      try {
        pdfRuntime.startWorker();
      } catch {
        // Ignore; the same runtime retries when the first PDF viewer is created.
      }
    }, 6000);
  });
}
